/* global RELEASE_VERSION */
import { useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { environment } from 'js/config'
import { useLocation } from 'react-router-dom'
import { amplitude } from 'js/analytics/adapters'
import { selectId, selectIsPremium } from 'js/store/userSlice'

// Off-production (staging, development) the Cookiebot consent banner can't be
// accepted because staging PR domains (pull-request-<N>.lifesum.xyz) aren't
// authorised in the Cookiebot domain group, which otherwise blocks analytics
// init. Force statistics consent there so Amplitude/Sentry can be QA'd. Gated
// on `environment`, so this is never true in production.
const bypassConsent = environment !== 'production'

function useTracking() {
  const prevLocation = useRef<Partial<Location>>()
  const location = useLocation()
  const userId = useSelector(selectId)
  const isPremium = useSelector(selectIsPremium)

  useEffect(() => {
    if (userId) {
      amplitude.setUserId(String(userId))
    } else {
      amplitude.setUserId(undefined)
    }
  }, [userId])

  useEffect(() => {
    if (userId) {
      amplitude.setUserProperties({
        account_type: isPremium ? 'premium' : 'freemium',
      })
    }
  }, [userId, isPremium])

  const loadTrackingScripts = useCallback(function loadTracking() {
    if (!bypassConsent && !window.Cookiebot) return
    if (navigator.userAgent.match('DisableForTesting')) return

    setTimeout(() => {
      addTrackers()
    }, 100)
  }, [])

  useEffect(() => {
    // Returning visitors have a stored consent answer, so Cookiebot can fire
    // CookiebotOnConsentReady before this effect registers its listener below,
    // leaving trackers uninitialised until the first scroll or navigation.
    // Checking once on mount catches that case: loadTrackingScripts() bails
    // when Cookiebot hasn't loaded yet, and addTrackers() re-checks the consent
    // flags, so this is a no-op while a banner response is still pending.
    loadTrackingScripts()

    window.addEventListener('scroll', loadTrackingScripts, {
      once: true,
    })
    document.addEventListener('loadTracking', loadTrackingScripts)
    window.addEventListener('CookiebotOnConsentReady', loadTrackingScripts)

    return () => {
      window.removeEventListener('scroll', loadTrackingScripts)
      document.removeEventListener('loadTracking', loadTrackingScripts)
      window.removeEventListener('CookiebotOnConsentReady', loadTrackingScripts)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // load tracking scripts when the user navigates to a new page
    if (
      typeof prevLocation.current !== 'undefined' &&
      prevLocation.current.pathname !== location.pathname
    ) {
      loadTrackingScripts()
    }
    prevLocation.current = location
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  function addTrackers() {
    if (!bypassConsent && !window.Cookiebot) return

    let trackers: Array<Promise<any>> = []

    // Marketing (Adjust/AWIN affiliate pixels) stays consent-gated everywhere,
    // including staging, to avoid polluting attribution from test environments.
    if (window.Cookiebot?.consent?.marketing)
      trackers = trackers.concat([
        // @ts-ignore tsc complains about side effects only dynamic imports that
        // should work according to: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/import#import_a_module_for_its_side_effects_only
        import('js/trackers/adjust'),
        // @ts-ignore
        import('js/trackers/awin'),
      ])

    // Statistics (Sentry + Amplitude) is force-enabled off-production so it can
    // be QA'd on staging without a working consent banner.
    if (bypassConsent || window.Cookiebot?.consent?.statistics)
      trackers = trackers.concat([
        // @ts-ignore tsc complains about side effects only dynamic imports that
        // should work according to: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/import#import_a_module_for_its_side_effects_only
        new Promise<void>((resolve) => {
          if (environment === 'development') resolve()
          return import('@sentry/browser').then((Sentry) => {
            resolve()
            window.sentryLoaded = true
            window['Sentry'] = Sentry
            Sentry.init({
              beforeSend: (event, hint) => {
                if (
                  event.request &&
                  event.request.headers &&
                  event.request.headers['User-Agent'] &&
                  event.request.headers['User-Agent'].match(
                    'Mozilla/5.0 (Java) outbrain',
                  )
                ) {
                  return null
                }

                const error = hint?.originalException
                if (!error || !(error instanceof Error)) {
                  return event
                }

                if (
                  error.message.match(
                    /onAPWebViewPause|webkitExitFullScreen|navBarMenuCollapses|callbackSharePreferencesNotify|onAPWebViewResume|setAdGeneParamsForiOSApp|Can't find variable: iom|pktAnnotationHighlighter|cancelado|annullato|Abgebrochen|geannuleerd|cancelled|отменено|avbruten|avbrutt|annulleret|geannuleerd|annulé/,
                  )
                ) {
                  return null
                }

                if (error.message.match(/^Loading chunk /)) {
                  event.fingerprint = [
                    'ChunkLoadError: Loading <chunkname> failed.',
                  ]
                  // Ignore 99% of these errors since we have too many and since
                  // we aren't totally sure what causes them, we keep 1% to be
                  // able to spot changes in the frequency of these etc.
                  if (Math.floor(Math.random() * 100) !== 0) {
                    return null
                  }
                }

                if (
                  error.message.match(
                    /^SecurityError: Blocked a frame with origin "https:\/\/lifesum.com"/,
                  )
                ) {
                  // Ignore 99% of these errors since we have too many and since
                  // we aren't totally sure what causes them, we keep 1% to be
                  // able to spot changes in the frequency of these etc.
                  if (Math.floor(Math.random() * 100) !== 0) {
                    return null
                  }
                }

                return event
              },
              dsn: 'https://63c3a9e439904e03b5b944a6207f67e2@sentry.io/1440941',
              environment:
                window.location.host === 'lifesum.com'
                  ? 'production'
                  : 'staging',
              release: `lifesum-web@${RELEASE_VERSION}`,
            })

            window.sentryQueue.forEach((error) => {
              if (Array.isArray(error)) {
                if (error[4]) {
                  Sentry.captureException(error[4], {
                    extra: {
                      file: error[1],
                      line: error[2],
                      col: error[3],
                    },
                  })
                } else {
                  Sentry.captureException(error[0])
                }
              } else {
                Sentry.withScope((scope) => {
                  scope.setExtras(error.errorInfo)
                  Sentry.captureException(error.error)
                })
              }
            })
          })
        }),
        // Amplitude Initialisation
        Promise.resolve(amplitude.init()),
      ])

    return Promise.all(trackers)
  }

  return
}

export default useTracking
